import Seo from "../components/Seo";
import { SITE } from "../config/site";

const sections = [
  {
    title: "1. Who we are",
    body: `${SITE.name} ("we", "us") is a small UK journey-matching service connecting passengers with TfL-licensed London Black Cab drivers who live in Essex. For the purposes of UK data protection law we are the data controller for the personal information you send us through this website. [PLACEHOLDER: insert the data controller's legal name and contact address before publication.]`,
  },
  {
    title: "2. What information we collect",
    body: "When you submit a journey request, driver application or contact message we collect the details you type into the form: your name, contact details (telephone number and email address), journey details (pickup and destination addresses, date, time, passenger numbers and luggage) and any notes you choose to add. Driver applicants additionally provide their Essex home area, usual travel times, preferred working areas, vehicle capacity and, optionally, their TfL Green Badge licence number. We never collect payment card or bank details.",
  },
  {
    title: "3. How we use your information",
    body: "Passenger details are used to review your journey request, check it against available drivers, contact you about confirmation, and — where a journey is confirmed — share the necessary contact and pickup details privately with the assigned driver. Driver application details are used to review and verify applications. We do not sell personal data, share it for marketing, or publish it.",
  },
  {
    title: "4. Lawful basis",
    body: "We process your information to take steps at your request before entering into a contract (arranging a journey or reviewing a driver application) and for our legitimate interests in operating a small journey-matching service. [Draft note: confirm the lawful basis wording with a legal adviser before publication.]",
  },
  {
    title: "5. Driver applications and licence documents",
    body: "Driver applicants provide contact details, their TfL Green Badge licence number and expiry date, and a photo or PDF of their licence for manual verification. Licence documents are transmitted privately to the business inbox and then held in private, access-controlled storage available only to the business owner. They are never published, given public links, or shared with other drivers or passengers. Unsuccessful applicants' licence documents are deleted once the review is complete; documents of drivers who leave the network are deleted on departure. Applications are never approved automatically.",
  },
  {
    title: "6. Who processes your data",
    body: "Form submissions are processed by our form services (FormSubmit and Web3Forms) and delivered to our business email inbox. These providers act as data processors on our behalf. Passenger contact and location details are shared with the single assigned driver only when a journey requires it — never in group messages or publicly.",
  },
  {
    title: "7. How long we keep your information",
    body: "Booking records are kept only for as long as needed to manage the journey, handle any queries and meet accounting or legal obligations, after which they are securely deleted. [Draft note: set a specific retention period, e.g. 12 months for unfilled requests and 6 years for completed-journey records, before publication.]",
  },
  {
    title: "8. Your rights",
    body: "Under UK GDPR you have the right to access, correct, erase, restrict or object to our processing of your personal data, and the right to data portability. To exercise any of these rights, contact us using the email address on our Contact page. You also have the right to complain to the Information Commissioner's Office (ICO) at ico.org.uk.",
  },
  {
    title: "9. Security",
    body: "This website does not store your information on its own servers. Submissions travel over encrypted connections to our form processor and business email account, which is protected with strong passwords and two-factor authentication. Booking records are held in private, access-controlled storage — never in public files or public code repositories.",
  },
  {
    title: "10. Cookies and analytics",
    body: "This website does not use advertising or tracking cookies. [Draft note: if analytics are added later, update this section and add a cookie notice.]",
  },
  {
    title: "11. Changes to this policy",
    body: "We may update this privacy notice from time to time. The latest version will always be published on this page.",
  },
];

export default function Privacy() {
  return (
    <main data-testid="privacy-page" className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-20">
      <Seo
        title="Privacy Policy"
        description="How Essex Black Cab collects, uses, stores and protects your personal information under UK GDPR."
      />
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">Legal</p>
      <h1 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">Privacy Policy</h1>
      <p
        data-testid="privacy-draft-banner"
        className="mt-5 border border-[rgba(212,175,55,0.4)] bg-[rgba(212,175,55,0.08)] p-4 text-sm leading-relaxed text-[#D4D4D8]"
      >
        Draft privacy notice — this document requires review against your final business details
        and legal advice before live publication. Items in [brackets] must be completed.
      </p>
      <p className="mt-4 text-sm text-[#71717A]">Last updated: [PLACEHOLDER — insert date]</p>

      <div className="mt-10 space-y-9">
        {sections.map((s) => (
          <section key={s.title}>
            <h2 className="text-xl font-semibold text-white">{s.title}</h2>
            <p className="mt-3 text-sm leading-relaxed text-[#A1A1AA] sm:text-base">{s.body}</p>
          </section>
        ))}
      </div>

      {SITE.email && (
        <p className="mt-12 border-t border-[#27272A] pt-6 text-sm text-[#A1A1AA]">
          Contact for privacy matters:{" "}
          <a href={`mailto:${SITE.email}`} className="focus-gold text-[#D4AF37] underline-offset-4 hover:underline">
            {SITE.email}
          </a>
        </p>
      )}
      {!SITE.email && (
        <p className="mt-12 border-t border-[#27272A] pt-6 text-sm text-[#A1A1AA]">
          For privacy matters, please use our{" "}
          <a href="/contact" className="focus-gold text-[#D4AF37] underline-offset-4 hover:underline">
            contact page
          </a>
          .
        </p>
      )}
    </main>
  );
}
